// The team report (Taylor's mockup, 16 Oct): a group's results as one picture, to spot gaps.
// Shared by the admin (staff) and the leader's page. First names only, in domain coverage;
// the map's dots carry no names.

import type { ReactNode } from "react";
import { domainColor } from "../lib/domainColors";
import { GIFT_EXPLAINERS } from "../lib/giftExplainers";
import { Term } from "./Term";
import { ARC_BAR_COLOR, BodyAtWorkCycle } from "./BodyAtWork";

export interface TeamReportData {
  people: {
    members: number;
    finished: number;
    /** First names of finished members whose scoring v4 result has lower confidence. Always [] for a v1-only team. */
    lowerConfidence: string[];
  };
  quadrants: Record<"Activated" | "Drifting" | "Caged" | "Fragmented", number>;
  dots: { freedom: number; clarity: number }[];
  clear: number;
  free: number;
  domains: { code: string; name: string; primary: string[]; secondary: string[]; flag: "heavy" | "thin" | "gap" | null }[];
  /** `domain` is null for the scoring v4 gifts: they have none. */
  gifts: { gift: string; domain: string | null; count: number }[];
  /** Finished people counted in `gifts`: fewer than `people.finished` when the team mixes test versions. */
  giftsCounted: number;
  themes: { code: string; name: string; average: number | null; lowest: boolean }[];
  /** The Body at Work: finished v4 members' pairings on the nine-gift cycle (null without v4 members; absent on older APIs). */
  cycle?: {
    counted: number;
    nodes: { gift: string; people: number }[];
    lines: { key: string; gifts: string[]; name: string; type: string; people: string[] }[];
    arcs: { arc: string; picks: number; of: number }[];
    insights: { kind: string; title: string; body: string }[];
  } | null;
  headline: {
    thinnestDomain: { code: string; name: string; leaders: number } | null;
    missingGifts: string[];
    lowestTheme: { code: string; name: string; average: number } | null;
  };
}

const FLAG: Record<"heavy" | "thin" | "gap", { label: string; tone: string }> = {
  heavy: { label: "Heavy", tone: "bg-ink text-ground" },
  thin: { label: "Thin", tone: "bg-ink text-ground" },
  gap: { label: "Gap risk", tone: "bg-danger text-ground" },
};

function Card({ title, note, children, testId }: { title: string; note?: string; children: ReactNode; testId?: string }) {
  return (
    <section className="rounded-[16px] border border-line bg-panel p-5" aria-label={title} data-testid={testId}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="heading text-[21px]">{title}</h2>
        {note ? <p className="text-[13px] text-muted">{note}</p> : null}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function GiftRow({ g }: { g: TeamReportData["gifts"][number] }) {
  return (
    <li
      className={`flex justify-between rounded-[8px] px-2.5 py-1.5 text-[13px] ${g.count === 0 ? "border border-dashed border-danger text-danger" : "bg-panel"}`}
    >
      <span className="font-semibold">
        {GIFT_EXPLAINERS[g.gift] ? (
          <Term
            tip={
              <>
                <strong className="block">Healthy</strong>
                {GIFT_EXPLAINERS[g.gift]!.healthy}
                <strong className="mt-2 block">Weaponized (when freedom is low)</strong>
                {GIFT_EXPLAINERS[g.gift]!.weaponized}
              </>
            }
          >
            {g.gift}
          </Term>
        ) : (
          g.gift
        )}
      </span>
      <span className="tabular-nums">{g.count}</span>
    </li>
  );
}

function Headline({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="rounded-[16px] border border-line bg-panel p-4">
      <p className="text-[13px] font-semibold text-muted">{label}</p>
      <p className="heading mt-1 text-[24px] leading-tight">{value}</p>
      <p className="mt-1 text-[13px] text-muted">{note}</p>
    </div>
  );
}

/** Freedom bottom to top, clarity left to right, as on each person's map. No names. */
function HealthMap({ r }: { r: TeamReportData }) {
  const size = 280;
  const pad = 26;
  const inner = size - pad * 2;
  const x = (clarity: number) => pad + (clarity / 100) * inner;
  const y = (freedom: number) => pad + (1 - freedom / 100) * inner;
  const label = (q: keyof TeamReportData["quadrants"]) => `${q.toUpperCase()} · ${r.quadrants[q]}`;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="mx-auto w-full max-w-[360px]" role="img" aria-label={`Team map: ${r.quadrants.Activated} Activated, ${r.quadrants.Drifting} Drifting, ${r.quadrants.Caged} Caged, ${r.quadrants.Fragmented} Fragmented.`}>
      <rect x={pad} y={pad} width={inner} height={inner} rx={10} fill="var(--color-soft)" stroke="var(--color-line)" />
      <line x1={size / 2} y1={pad} x2={size / 2} y2={size - pad} stroke="var(--color-line)" />
      <line x1={pad} y1={size / 2} x2={size - pad} y2={size / 2} stroke="var(--color-line)" />
      <g fontSize={8} fontWeight={700} letterSpacing="0.06em" fill="var(--color-muted)">
        <text x={pad + 6} y={pad + 14}>{label("Drifting")}</text>
        <text x={size - pad - 6} y={pad + 14} textAnchor="end">{label("Activated")}</text>
        <text x={pad + 6} y={size - pad - 8}>{label("Fragmented")}</text>
        <text x={size - pad - 6} y={size - pad - 8} textAnchor="end">{label("Caged")}</text>
        <text x={size / 2} y={pad - 10} textAnchor="middle">FREE</text>
        <text x={size / 2} y={size - 8} textAnchor="middle">BOUND</text>
        <text x={12} y={size / 2} textAnchor="middle" transform={`rotate(-90 12 ${size / 2})`}>UNCLEAR</text>
        <text x={size - 12} y={size / 2} textAnchor="middle" transform={`rotate(90 ${size - 12} ${size / 2})`}>CLEAR</text>
      </g>
      {r.dots.map((d, i) => (
        <circle key={i} data-testid="team-dot" cx={x(d.clarity)} cy={y(d.freedom)} r={6} fill="var(--color-accent-text)" stroke="var(--color-panel)" strokeWidth={2} />
      ))}
    </svg>
  );
}

export function TeamReport({ r }: { r: TeamReportData }) {
  const n = r.people.finished;
  if (n === 0) {
    return (
      <p className="rounded-[16px] border border-line bg-panel p-5 text-[15px] text-muted" data-testid="team-report-empty">
        No one has finished yet ({r.people.members} joined). The report fills in as people finish.
      </p>
    );
  }
  const giftsByDomain = r.domains.map((d) => ({ ...d, gifts: r.gifts.filter((g) => g.domain === d.code) }));
  // Scoring v4's gifts have no domain: one flat list instead of the domain boxes.
  const flatGifts = r.gifts.length > 0 && r.gifts.every((g) => g.domain === null);
  return (
    <div className="flex flex-col gap-4" data-testid="team-report">
      <p className="text-[14px] text-muted">
        {n} of {r.people.members} finished{n < r.people.members ? ": the report fills in as the rest finish" : ""}.
      </p>
      {r.people.lowerConfidence.length > 0 ? (
        <p className="-mt-2 text-[14px] text-muted" data-testid="team-lower-confidence">
          Lower confidence: {r.people.lowerConfidence.join(", ")}
        </p>
      ) : null}
      {r.giftsCounted < n ? (
        <p className="-mt-2 text-[14px] text-muted" data-testid="team-gifts-counted">
          Gift coverage counts {r.giftsCounted} of {n} people: the others took a version of the test with a different gift list.
        </p>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Headline label="Team health" value={`${r.quadrants.Activated} of ${n}`} note={`are Activated. ${r.quadrants.Caged} are Caged.`} />
        {r.headline.thinnestDomain ? (
          <Headline
            label="Thinnest domain"
            value={r.headline.thinnestDomain.name}
            note={`${r.headline.thinnestDomain.leaders} ${r.headline.thinnestDomain.leaders === 1 ? "person leads" : "people lead"} with it.`}
          />
        ) : null}
        <Headline
          label={r.headline.missingGifts.length > 1 ? "Missing gifts" : "Missing gift"}
          value={r.headline.missingGifts.length ? r.headline.missingGifts.join(", ") : "None"}
          note={r.headline.missingGifts.length ? "No one has it in their top three." : "Every gift is in someone's top three."}
        />
        {r.headline.lowestTheme ? (
          <Headline label="Team's lowest theme" value={r.headline.lowestTheme.name} note={`Average ${r.headline.lowestTheme.average} of 100.`} />
        ) : null}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Team health map" note={`${n} people · no names`}>
          <HealthMap r={r} />
          <p className="mt-3 text-[14px] text-muted">
            {r.clear} of {n} sit right of centre (clear). {r.free} of {n} sit above it (free).
          </p>
        </Card>

        <Card title="Domain coverage" note="Home base · plus second" testId="domain-coverage">
          <div className="grid grid-cols-2 gap-2">
            {r.domains.map((d) => (
              <div key={d.code} className="rounded-[12px] bg-soft p-3" data-testid={`domain-${d.code}`}>
                <p className="eyebrow flex items-center gap-1.5" style={{ color: domainColor(d.code).text }}>
                  <span aria-hidden="true" className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: domainColor(d.code).fill }} />
                  {d.name}
                </p>
                <p className="heading text-[28px] leading-none">{d.primary.length}</p>
                {d.primary.length ? <p className="mt-1 text-[13px] font-semibold">{d.primary.join(", ")}</p> : null}
                {d.secondary.length ? <p className="text-[12px] text-muted">Second: {d.secondary.join(", ")}</p> : null}
                {d.flag ? <span className={`mt-2 inline-block rounded-full px-2 py-0.5 text-[11px] font-bold ${FLAG[d.flag].tone}`}>{FLAG[d.flag].label}</span> : null}
              </div>
            ))}
          </div>
        </Card>

        <Card title="Gift coverage" note="People with the gift in their top three" testId="gift-coverage">
          {flatGifts ? (
            <div className="rounded-[12px] bg-soft p-3">
              <ul className="flex flex-col gap-1.5" data-testid="gift-coverage-flat">
                {r.gifts.map((g) => (
                  <GiftRow key={g.gift} g={g} />
                ))}
              </ul>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {giftsByDomain.map((d) => (
                <div key={d.code} className="rounded-[12px] bg-soft p-3">
                  <p className="eyebrow flex items-center gap-1.5" style={{ color: domainColor(d.code).text }}>
                    <span aria-hidden="true" className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: domainColor(d.code).fill }} />
                    {d.name}
                  </p>
                  <ul className="mt-2 flex flex-col gap-1.5">
                    {d.gifts.map((g) => (
                      <GiftRow key={g.gift} g={g} />
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </Card>

        {r.cycle ? <TeamCycleCard cycle={r.cycle} finished={r.people.finished} /> : null}

        <Card title="Freedom themes" note="Team average · higher is freer" testId="freedom-themes">
          <div className="grid grid-cols-2 gap-2">
            {r.themes.map((t) => (
              <div key={t.code} className={`rounded-[12px] p-3 ${t.lowest ? "border border-danger bg-soft" : "bg-soft"}`}>
                <p className="eyebrow text-accent-text">{t.name}</p>
                <p className="heading text-[28px] leading-none">{t.average ?? "—"}</p>
                {t.lowest ? <span className="mt-2 inline-block rounded-full bg-ink px-2 py-0.5 text-[11px] font-bold text-ground">Lowest</span> : null}
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

/**
 * "Team on the cycle" (v4.1 doc): each person's pairing as a line across the nine-gift cycle,
 * thicker when shared; how many carry each gift (nobody: a dashed outline); each arc's share of
 * the team's picks; and the insights for leaders. v4 members only: the first engine's ten gifts
 * aren't on the cycle. First names only, never bound forms.
 */
function TeamCycleCard({ cycle, finished }: { cycle: NonNullable<TeamReportData["cycle"]>; finished: number }) {
  const counts = Object.fromEntries(cycle.nodes.map((n) => [n.gift, n.people]));
  // One card each for the team-wide insights; partner suggestions together, one line a person.
  const insights = cycle.insights.filter((i) => i.kind !== "partner");
  const partners = cycle.insights.filter((i) => i.kind === "partner");
  return (
    <section className="rounded-[16px] border border-line bg-panel p-5 lg:col-span-2" aria-label="Team on the cycle" data-testid="team-cycle">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="heading text-[21px]">Team on the cycle</h2>
        <p className="text-[13px] text-muted">
          Each line is one person's top two gifts{cycle.counted < finished ? ` · ${cycle.counted} of ${finished} people (the v4 test)` : ""}
        </p>
      </div>
      <div className="mt-4 grid gap-6 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
        <BodyAtWorkCycle
          counts={counts}
          lines={cycle.lines.map((l) => ({ gifts: [l.gifts[0]!, l.gifts[1]!] as [string, string], weight: l.people.length }))}
          label={`The team on the nine-gift cycle. ${cycle.nodes.map((n) => `${n.gift}: ${n.people}`).join(", ")}.`}
        />
        <div className="flex flex-col gap-4">
          <div className="rounded-[12px] bg-soft p-4">
            <p className="text-[15px] font-semibold">Where the team sits</p>
            <ul className="mt-3 flex flex-col gap-2.5">
              {cycle.arcs.map((a) => (
                <li key={a.arc}>
                  <p className="text-[14px]">
                    <span className="font-semibold">{a.arc}</span> · {a.picks} of {a.of} picks
                  </p>
                  <div className="mt-1 h-2 rounded-full bg-line">
                    <div className="h-full rounded-full" style={{ width: `${a.of ? (a.picks / a.of) * 100 : 0}%`, backgroundColor: ARC_BAR_COLOR[a.arc] }} />
                  </div>
                </li>
              ))}
            </ul>
          </div>
          {insights.length ? (
            <ul className="flex flex-col gap-3">
              {insights.map((i) => (
                <li key={`${i.kind}-${i.title}`} className="rounded-[12px] border border-line p-3">
                  <p className="text-[15px] font-semibold">{i.title}</p>
                  <p className="mt-1 text-[14px] leading-snug text-muted">{i.body}</p>
                </li>
              ))}
            </ul>
          ) : null}
          {partners.length ? (
            <div className="rounded-[12px] border border-line p-3" data-testid="partners">
              <p className="text-[15px] font-semibold">Who to pair</p>
              <ul className="mt-2 flex flex-col gap-1.5 text-[14px] leading-snug">
                {partners.map((i) => (
                  <li key={i.title}>
                    <span className="font-semibold">{i.title}:</span> <span className="text-muted">{i.body}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
